import React from 'react'
import styled, {keyframes} from 'styled-components'
import {Clock} from 'lucide-react'
import {useSessionTimeout} from '~/src/context/SessionTimeoutContext'
import {Button} from '~/src/design-system/Primitives'

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const Overlay = styled.div`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 1400;
  animation: ${slideIn} 0.3s ease-out;
`

const AlertContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  min-width: 400px;
  max-width: 500px;
  padding: var(--space-4);
  border-radius: var(--radius-sm);
  background-color: #ed6c02;
  color: var(--white);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`

const IconWrapper = styled.div`
  margin-top: 2px;
  flex-shrink: 0;
`

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`

const ActionContainer = styled.div`
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-3);
`

export function SessionWarningNotification() {
  const {showWarning, minutesRemaining, staySignedIn, logout} = useSessionTimeout()

  if (!showWarning) return null

  return (
    <Overlay data-testid="session-warning-snackbar">
      <AlertContainer data-testid="session-warning-alert">
        <IconWrapper>
          <Clock size={24} />
        </IconWrapper>
        <MessageContainer data-testid="session-warning-message-container">
          <TitleRow data-testid="session-warning-title-row">
            <p
              data-testid="session-warning-title"
              style={{fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', margin: 0}}>
              Session Expiring Soon
            </p>
          </TitleRow>

          <p
            data-testid="session-warning-expiry-text"
            style={{fontSize: 'var(--fs-caption)', margin: 0}}>
            Your session will expire in{' '}
            <strong>
              {minutesRemaining} minute{minutesRemaining !== 1 ? 's' : ''}
            </strong>{' '}
            due to inactivity.
          </p>

          <p
            data-testid="session-warning-save-text"
            style={{fontSize: 'var(--fs-caption)', opacity: 0.9, margin: 0}}>
            Please save your work to avoid losing changes.
          </p>

          <ActionContainer>
            <Button
              data-testid="session-warning-stay-signed-in-button"
              variant="secondary"
              size="sm"
              onClick={staySignedIn}>
              Stay signed in
            </Button>
            <Button
              data-testid="session-warning-logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}>
              Sign out now
            </Button>
          </ActionContainer>
        </MessageContainer>
      </AlertContainer>
    </Overlay>
  )
}

export default SessionWarningNotification
